import { forwardRef } from 'react'

import { cn } from '@/lib/utils'
import { FxIcon } from './FxIcon'

/**
 * The top row of every page inside a template: "← Zurück" at the left, the
 * template or document name centred after a `primary` `description` icon, a
 * pencil to rename it where that is allowed, and the `primary` info button at
 * the right.
 *
 * Ported from the design system's `DetailHeader`, with three additions the
 * artboards need: the back link has a label of its own (they say "Übersicht"
 * and "Zurück zum Ergebnis" as often as "Zurück") and may be an `<a href>`
 * rather than a button; the title can be left-aligned (`align="start"`, the
 * editor); and `actions` takes whatever a screen puts beside the info button.
 *
 * The name ellipsises rather than wrapping, so the header keeps one row at
 * phone width between the hamburger and the info button.
 */
export interface FxDetailHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string
  /** Shows the rename pencil. Without `onEdit` it does nothing, so pass both. */
  editable?: boolean
  onEdit?: () => void
  /** Shows the info button. */
  info?: boolean
  onInfo?: () => void
  /** What the info button is about, for people who cannot see where it sits. */
  infoLabel?: string
  /** `false` hides the back affordance entirely. */
  onBack?: false | (() => void)
  /** Renders the back affordance as a link. Takes precedence over `onBack`. */
  backHref?: string
  backLabel?: string
  /** The editor puts the name at the left instead of centring it. */
  align?: 'center' | 'start'
  /** Anything else on the right, before the info button. */
  actions?: React.ReactNode
}

export const FxDetailHeader = forwardRef<HTMLDivElement, FxDetailHeaderProps>(
  (
    {
      title,
      editable = false,
      onEdit,
      info = false,
      onInfo,
      infoLabel = 'Hilfe zu dieser Seite',
      onBack,
      backHref,
      backLabel = 'Zurück',
      align = 'center',
      actions,
      className,
      ...props
    },
    ref,
  ) => {
    const backContent = (
      <>
        <FxIcon name="arrow_back" size={28} className="text-text-muted" />
        {backLabel}
      </>
    )
    const backClassName =
      'inline-flex min-h-control shrink-0 items-center gap-3 text-body text-text no-underline transition-colors hover:text-primary-text'
    return (
      <div
        ref={ref}
        className={cn('flex min-h-list-row items-center gap-5', className)}
        {...props}
      >
        {backHref ? (
          <a href={backHref} className={backClassName}>
            {backContent}
          </a>
        ) : (
          onBack !== false && (
            <button type="button" className={backClassName} onClick={onBack || undefined}>
              {backContent}
            </button>
          )
        )}

        <div
          className={cn(
            'flex min-w-0 flex-1 items-center gap-3 text-body',
            align === 'center' ? 'justify-center' : 'justify-start',
          )}
        >
          <FxIcon name="description" size={24} className="text-primary-strong" />
          <span className="truncate">{title}</span>
          {editable && (
            <button
              type="button"
              aria-label="Umbenennen"
              className="inline-flex shrink-0 rounded-sm p-[2px] text-text transition-colors hover:text-primary-text"
              onClick={onEdit}
            >
              <FxIcon name="edit" size={24} />
            </button>
          )}
        </div>

        {actions}
        {info && (
          <button
            type="button"
            aria-label={infoLabel}
            className="inline-flex shrink-0 text-primary-strong"
            onClick={onInfo}
          >
            <FxIcon name="info" size={24} filled />
          </button>
        )}
      </div>
    )
  },
)
FxDetailHeader.displayName = 'FxDetailHeader'
